import { z } from 'zod';
import { BidStatus } from './car-enums';
import { DealStateSchema } from './deal';
import { InfraCurrencySchema } from './infra-costs';
import { KycStatusSchema } from './kyc';
import { RELAY_CANCEL_REASONS, RelayRejectReasonSchema, RelayStatusSchema } from './relay';
/**
 * Notification events (Phase 8) — the NORMALISED view of an `outbox_events`
 * row that the notification dispatcher acts on.
 *
 * This is deliberately NOT the outbox wire message. The outbox carries four
 * different envelope shapes on purpose:
 *
 *   1. user channel   {type:'bid-status-change'|'bid-update', data:{...}}
 *   2. audit rows     {type:'relay.outcome'|'kyc.status_changed'|..., data:{...}}
 *   3. flat rows      {bidId, carId, userId, ...} (relay.amount_changed / auto_cancelled)
 *   4. admin firehose {operationType, fullDocument, updateDescription?, ...}
 *
 * Shapes 1-3 carry a business event a human should hear about; shape 4 mirrors
 * the Mongo change stream for the admin UI and is NOT a notification source
 * (the same business event already produced a row of shape 1 or 3). The
 * API-side normaliser flattens 1-3 and then PARSES the result with
 * {@link NotificationEventSchema} — it never casts, so a producer that drifts
 * is caught at the dispatcher instead of rendering "undefined" into a Telegram
 * message.
 *
 * Every variant carries `eventId` (the outbox `_id`) so delivery can be
 * deduplicated per channel per recipient, and `occurredAt` (the outbox
 * `createdAt`) so a dedup key stays unique across repeats on the same lot.
 */
/** The 4 canonical bid statuses, as a zod enum over the const object. */
export const BidStatusEnumSchema = z.enum([
    BidStatus.Active,
    BidStatus.Outbid,
    BidStatus.Cancelled,
    BidStatus.Won,
]);
/** Notification topics a user can reason about. */
export const NotificationTopicSchema = z.enum([
    'bids',
    'kyc',
    'deals',
    'marketing',
    'account',
    // mobile-app-expo phase 3: "a new lot matches your saved search". Toggleable,
    // default ON, NOT transactional (it is a convenience, not the user's money).
    'saved_search',
]);
/**
 * TRANSACTIONAL topics describe something that happened to the user's own
 * money or legal standing, so they cannot be switched off for the in-app
 * channel — a user must always be able to find out that they won a lot.
 * `marketing` is the only opt-in topic.
 */
export const TRANSACTIONAL_TOPICS = [
    'bids',
    'kyc',
    'deals',
    'account',
];
/**
 * `account` has no toggle at all: it carries password resets and email
 * verification, which are security mail rather than preferences. It is kept
 * OUT of the stored `topics` object for exactly that reason.
 */
export const UNTOGGLEABLE_TOPICS = ['account'];
const eventBase = {
    /** The outbox `_id`. Present on every normalised event. */
    eventId: z.string().min(1),
    /** The outbox `createdAt`, ISO-8601. */
    occurredAt: z.string().min(1),
};
/** A bid of a client changed status (Outbid / Won / Not Won / Under Review). */
export const BidStatusChangedNotificationSchema = z.object({
    ...eventBase,
    type: z.literal('bid.status_changed'),
    bidId: z.string().min(1),
    carId: z.string().min(1),
    userId: z.string().min(1),
    newStatus: BidStatusEnumSchema,
    previousStatus: BidStatusEnumSchema.nullish(),
    bidAmount: z.number().nullish(),
    /**
     * Phase 10: WHY the platform moved the bid, when it did so automatically.
     * `tender_closed_unrelayed` lets the client template say "the auction closed
     * and your bid was never relayed" instead of a bare "lot not won".
     */
    relayCancelReason: z.enum(RELAY_CANCEL_REASONS).nullish(),
    /**
     * Phase 13: the operator tried to relay the bid and the upstream auction
     * refused it (`relayStatus: 'upstream_rejected'`). Lets the client template
     * say "менеджер не зміг передати ставку: <reason>".
     */
    relayRejectReason: RelayRejectReasonSchema.nullish(),
});
/** A client placed a bid. */
export const BidInsertedNotificationSchema = z.object({
    ...eventBase,
    type: z.literal('bid.inserted'),
    bidId: z.string().min(1),
    carId: z.string().min(1),
    userId: z.string().min(1),
    amount: z.number(),
    status: BidStatusEnumSchema.nullish(),
});
/** A client raised or edited their bid. */
export const BidUpdatedNotificationSchema = z.object({
    ...eventBase,
    type: z.literal('bid.updated'),
    bidId: z.string().min(1),
    carId: z.string().min(1),
    userId: z.string().min(1),
    amount: z.number(),
    status: BidStatusEnumSchema.nullish(),
});
/** The relay desk's locked amount drifted (a proxy raised a locked bid). */
export const RelayAmountChangedNotificationSchema = z.object({
    ...eventBase,
    type: z.literal('relay.amount_changed'),
    bidId: z.string().min(1),
    carId: z.string().min(1),
    userId: z.string().min(1),
    oldAmount: z.number(),
    newAmount: z.number(),
    relayStatus: RelayStatusSchema,
    source: z.string().nullish(),
});
/** A relayed bid was auto-cancelled (outbid while captured/queued). */
export const RelayAutoCancelledNotificationSchema = z.object({
    ...eventBase,
    type: z.literal('relay.auto_cancelled'),
    bidId: z.string().min(1),
    carId: z.string().min(1),
    userId: z.string().min(1),
    bidAmount: z.number(),
    previousRelayStatus: RelayStatusSchema,
    newRelayStatus: RelayStatusSchema,
    reason: z.enum(RELAY_CANCEL_REASONS),
});
/** An admin recorded the tender outcome for a relayed bid. */
export const RelayOutcomeNotificationSchema = z.object({
    ...eventBase,
    type: z.literal('relay.outcome'),
    bidId: z.string().min(1),
    carId: z.string().min(1),
    userId: z.string().min(1),
    bidAmount: z.number(),
    who: z.string().min(1),
    previousRelayStatus: RelayStatusSchema,
    newRelayStatus: RelayStatusSchema,
    outcome: z.enum(['won', 'lost']),
});
/** A client's KYC status moved. */
export const KycStatusChangedNotificationSchema = z.object({
    ...eventBase,
    type: z.literal('kyc.status_changed'),
    userId: z.string().min(1),
    from: KycStatusSchema,
    to: KycStatusSchema,
    providerRef: z.string().nullish(),
});
/**
 * KYC defects (2026-09-30): an admin asked the client to upload an identity
 * document (`POST /api/admin/users/:userId/kyc/document-request`). `note` is
 * the admin's optional message to the client (≤ 500 chars).
 */
export const KycDocumentRequestedNotificationSchema = z.object({
    ...eventBase,
    type: z.literal('kyc.document_requested'),
    userId: z.string().min(1),
    /** Admin userId who asked. */
    requestedBy: z.string().min(1),
    note: z.string().nullish(),
});
/** A post-win deal moved along its ladder. */
export const DealStateChangedNotificationSchema = z.object({
    ...eventBase,
    type: z.literal('deal.state_changed'),
    dealId: z.string().min(1),
    wonCarId: z.string().nullish(),
    userId: z.string().nullish(),
    /** The lot (`alpicar_cars._id`), so the message can name the car. Absent on rows written before BUG-07. */
    carId: z.string().nullish(),
    from: DealStateSchema,
    to: DealStateSchema,
    who: z.string().min(1),
    override: z.boolean().nullish(),
});
const userAccountEvent = {
    ...eventBase,
    userId: z.string().min(1),
    email: z.string().nullish(),
};
/** Account lifecycle events (phase 9 owns their bodies via MailPort). */
export const UserRegisteredNotificationSchema = z.object({
    ...userAccountEvent,
    type: z.literal('user.registered'),
});
export const UserEmailVerificationRequestedNotificationSchema = z.object({
    ...userAccountEvent,
    type: z.literal('user.email_verification_requested'),
});
export const UserPasswordResetRequestedNotificationSchema = z.object({
    ...userAccountEvent,
    type: z.literal('user.password_reset_requested'),
});
/** The 4h / 1h operator warning windows before a tender closes. */
export const TenderClosingWindowSchema = z.enum(['4h', '1h']);
export const TENDER_CLOSING_WINDOWS = TenderClosingWindowSchema.options;
/**
 * A lot with an UNRELAYED bid (captured/queued) is about to close — the
 * operator must act (Phase 10 TenderCloseService). One event per bid per
 * window, deduplicated by the producer (`tg:closing:{bidId}:{window}`).
 */
export const TenderClosingSoonNotificationSchema = z.object({
    ...eventBase,
    type: z.literal('tender.closing_soon'),
    bidId: z.string().min(1),
    carId: z.string().min(1),
    userId: z.string().nullish(),
    bidAmount: z.number(),
    /** ISO-8601 auction end (the LIVE car date, not the bid snapshot). */
    closesAt: z.string().min(1),
    window: TenderClosingWindowSchema,
});
/**
 * The tender closed while the bid was still captured/queued — it was never
 * relayed, and the client bid is now 'Not Won' / relayStatus 'expired'
 * (Phase 10 TenderCloseService, same transaction as the flip).
 */
export const RelayExpiredNotificationSchema = z.object({
    ...eventBase,
    type: z.literal('relay.expired'),
    bidId: z.string().min(1),
    carId: z.string().min(1),
    userId: z.string().min(1),
    bidAmount: z.number(),
    previousRelayStatus: RelayStatusSchema,
    newRelayStatus: RelayStatusSchema,
    reason: z.enum(RELAY_CANCEL_REASONS),
    /** ISO-8601 close date the cron acted on. */
    tenderCloseAt: z.string().nullish(),
});
/**
 * Phase 13: the operator tried to place the bid upstream and the real auction
 * refused it (relayed/queued → upstream_rejected). Staff hear the reason and
 * the note; the client hears the reason through `bid.status_changed`.
 */
export const RelayRejectedNotificationSchema = z.object({
    ...eventBase,
    type: z.literal('relay.rejected'),
    bidId: z.string().min(1),
    carId: z.string().min(1),
    userId: z.string().min(1),
    bidAmount: z.number(),
    who: z.string().min(1),
    previousRelayStatus: RelayStatusSchema,
    newRelayStatus: RelayStatusSchema,
    rejectReason: RelayRejectReasonSchema,
    rejectNote: z.string().nullish(),
});
/**
 * Phase 13: a post-win deal has sat in one rung longer than its SLA
 * (DealSlaService, hourly, one alert per deal per UTC day). Staff-only.
 */
export const DealOverdueNotificationSchema = z.object({
    ...eventBase,
    type: z.literal('deal.overdue'),
    dealId: z.string().min(1),
    userId: z.string().nullish(),
    wonCarId: z.string().nullish(),
    carId: z.string().nullish(),
    bidId: z.string().nullish(),
    state: DealStateSchema,
    stateEnteredAt: z.string().min(1),
    slaHours: z.number(),
    overdueHours: z.number(),
});
/**
 * Infra costs (2026-10-14): a platform bill — GCP, Atlas, sms-fly, … — is due
 * in `remindDaysBefore` days (`before`), today (`due`), or is past due and
 * still unpaid (`overdue`, daily). InfraPaymentReminderService, 09:00 Kyiv,
 * one alert per service, due date, kind and day. Staff-only (admin group).
 */
export const InfraPaymentDueNotificationSchema = z.object({
    ...eventBase,
    type: z.literal('infra.payment_due'),
    serviceId: z.string().min(1),
    serviceName: z.string().min(1),
    /** `YYYY-MM-DD`. */
    dueDate: z.string().date(),
    amount: z.number().nonnegative(),
    currency: InfraCurrencySchema,
    amountUsd: z.number().nonnegative(),
    amountIsEstimate: z.boolean(),
    kind: z.enum(['before', 'due', 'overdue']),
    /** Days until the due date; negative when overdue. */
    daysUntil: z.number().int(),
});
/**
 * Admin deals & deposits (phase 3): an operator pressed «Нагадати» — the
 * client is asked to top up their CHF deposit. `depositChf` is the balance at
 * the moment of the reminder; `multiplier` is present for a deposit-mode
 * client (the ceiling they would get back). Staff are not told — they sent it.
 */
export const DepositTopupReminderNotificationSchema = z.object({
    ...eventBase,
    type: z.literal('deposit.topup_reminder'),
    userId: z.string().min(1),
    depositChf: z.number(),
    multiplier: z.number().nullish(),
    /** Admin userId who asked for the reminder. */
    requestedBy: z.string().min(1),
});
/**
 * Client finance (CFB phase 3): a client asked to top up their CHF deposit by
 * bank transfer or cash (`deposit_topup_requests`). Staff hear it in the admin
 * bot («Заявка на поповнення DEP-AT-0927 · CHF 3 000 · готівка») so the cash
 * desk / bank reconciliation knows what to expect. The client created it and
 * already sees the instructions — they are not told again.
 */
export const DepositTopupRequestedNotificationSchema = z.object({
    ...eventBase,
    type: z.literal('deposit.topup_requested'),
    userId: z.string().min(1),
    requestId: z.string().min(1),
    reference: z.string().min(1),
    amountChf: z.number(),
    method: z.enum(['bank', 'cash']),
});
/**
 * Client finance (CFB phase 3): a manager confirmed a top-up request — the
 * `deposit` ledger row was written in the same transaction. `amountChf` is
 * what was actually credited (may differ from the requested amount);
 * `method` is how the money arrived (`card` for an office terminal).
 */
export const DepositTopupConfirmedNotificationSchema = z.object({
    ...eventBase,
    type: z.literal('deposit.topup_confirmed'),
    userId: z.string().min(1),
    requestId: z.string().min(1),
    reference: z.string().min(1),
    amountChf: z.number(),
    method: z.enum(['bank', 'card', 'cash']),
    /** Deposit balance AFTER the credit. */
    depositAfterChf: z.number(),
});
/**
 * Client finance: a manager REJECTED a top-up request (e.g. the money never
 * arrived). Written in the same transaction as the `open → rejected` flip,
 * like `deposit.topup_confirmed` — the client must be told, otherwise the
 * request just disappears from their open list. `reason` is the manager's
 * own words, shown to the client verbatim.
 */
export const DepositTopupRejectedNotificationSchema = z.object({
    ...eventBase,
    type: z.literal('deposit.topup_rejected'),
    userId: z.string().min(1),
    requestId: z.string().min(1),
    reference: z.string().min(1),
    /** The amount the client announced. */
    amountChf: z.number(),
    reason: z.string().min(1),
});
/**
 * Admin deals & deposits (phase 6): an operator pressed «Нагадати» on an
 * unpaid invoice. `outstandingChf` is what the invoice still owes at the
 * moment of the reminder (`invoiceOutstandingChf`), `dueAt` its deadline
 * (ISO-8601) and `overdue` whether that deadline had passed. Staff are not
 * told — they sent it.
 */
export const InvoicePaymentReminderNotificationSchema = z.object({
    ...eventBase,
    type: z.literal('invoice.payment_reminder'),
    userId: z.string().min(1),
    invoiceNo: z.string().min(1),
    outstandingChf: z.number(),
    dueAt: z.string().min(1),
    overdue: z.boolean(),
    /** Admin userId who asked for the reminder. */
    requestedBy: z.string().min(1),
});
/**
 * Owner decision (a), 2026-09-28: the client refused to pay for a won car and
 * the operator withheld the deposit (`POST /api/admin/deals/:dealId/forfeit-
 * deposit`). The client is told WHAT was withheld and for which car — the
 * operator's internal `reason` is deliberately not part of this shape (the
 * outbox row carries it for the audit; parsing drops it). `totalChf` is the
 * whole penalty: `depositChf` + `prepaymentChf` + `paidChf`. Staff are not
 * told — they did it.
 */
export const DealDepositForfeitedNotificationSchema = z.object({
    ...eventBase,
    type: z.literal('deal.deposit_forfeited'),
    userId: z.string().min(1),
    dealId: z.string().min(1),
    carId: z.string().nullish(),
    invoiceNo: z.string().min(1),
    depositChf: z.number(),
    prepaymentChf: z.number(),
    paidChf: z.number(),
    totalChf: z.number(),
});
/**
 * Admin client finances: an operator recorded money the client paid
 * (`POST /api/admin/users/:userId/payments`) and allocated it — to the deposit
 * (`depositChf`, null when none of it went there) and/or to invoices. Written
 * in the same transaction as the ledger row / settlements, like
 * `deposit.topup_confirmed`. `depositAfterChf` is the deposit after the payment.
 */
export const PaymentReceivedNotificationSchema = z.object({
    ...eventBase,
    type: z.literal('payment.received'),
    userId: z.string().min(1),
    paymentId: z.string().min(1),
    /** The whole payment in CHF. */
    amountChf: z.number(),
    method: z.enum(['bank', 'card', 'cash']),
    /** The part credited to the deposit; null when nothing went there. */
    depositChf: z.number().nullable(),
    invoices: z.array(z.object({ invoiceNo: z.string().min(1), amountChf: z.number() })),
    depositAfterChf: z.number(),
});
/**
 * mobile-app-expo phase 3: a lot that was just INGESTED matches one of the
 * client's active saved searches (`user_filters`). Produced by IngestService
 * next to the (unchanged) Telegram saved-search hook, once per lot + user.
 * Delivered by the `mobile-push` channel only; Telegram keeps its own path.
 *
 * `lotUrl` is the lot's canonical PUBLIC page
 * (`<webBaseUrl>/auctions/<source>/<listing_id>`), the same URL the sitemap
 * publishes and the native app claims as a Universal / App Link.
 */
export const SavedSearchMatchedNotificationSchema = z.object({
    ...eventBase,
    type: z.literal('saved_search.matched'),
    userId: z.string().min(1),
    /** `user_filters._id`. */
    filterId: z.string().min(1),
    /** Display label built from the filter's criteria; may be empty ("any car"). */
    filterName: z.string(),
    /** `alpicar_cars._id`. */
    carId: z.string().min(1),
    listingId: z.string().min(1),
    lotUrl: z.string().min(1),
    brand: z.string().nullish(),
    model: z.string().nullish(),
    price: z.number().nullish(),
});
/**
 * Owner, 2026-10-25: an invoice was issued — by an operator, or automatically
 * when the bid was marked won (`automatic`). Written in the SAME transaction as
 * the invoice and its deposit prepayment. `prepaymentChf` is what the deposit
 * covered, `dueChf` what is left to pay by `dueAt` (ISO-8601; 0 = settled by
 * the deposit at issue). The client AND staff are told.
 */
export const InvoiceIssuedNotificationSchema = z.object({
    ...eventBase,
    type: z.literal('invoice.issued'),
    userId: z.string().min(1),
    invoiceNo: z.string().min(1),
    kind: z.enum(['car', 'extra', 'sub', 'other']),
    dealId: z.string().nullish(),
    carId: z.string().nullish(),
    totalChf: z.number(),
    prepaymentChf: z.number(),
    dueChf: z.number(),
    dueAt: z.string().min(1),
    /** Admin userId who issued it (for an automatic invoice: who marked the win). */
    issuedBy: z.string().min(1),
    automatic: z.boolean(),
});
/**
 * admin-mobile-app phase 5: a client submitted a KYC document for review
 * (`POST /api/users/:id/documents` and `.../documents/upload`), written in the
 * SAME transaction as the `userDocuments` row. Staff-only — the admin app's
 * push channel (topic `kyc`); the client just uploaded it and is not told.
 * `documentType` is the client-declared type (passport, id_card, …).
 */
export const KycDocumentSubmittedNotificationSchema = z.object({
    ...eventBase,
    type: z.literal('kyc.document_submitted'),
    userId: z.string().min(1),
    documentId: z.string().min(1),
    documentType: z.string().min(1),
});
/**
 * admin-mobile-app phase 5 (D-5): a bid is STILL unrelayed (captured/queued,
 * claimed or not) and its lot closes within the escalation threshold
 * (`ADMIN_ESCALATION_MINUTES`, default 15). The critical admin push: every
 * active admin device, regardless of claim, topic or quiet hours. One event per
 * bid, deduplicated by the producer (`adm:esc:{bidId}`, AdminEscalationJob).
 * `claimedBy` is the operator holding a live (non-stale) claim, else null.
 * Deliberately NOT carried by the admin Telegram channel (it keeps its own
 * 4h/1h `tender.closing_soon` windows).
 */
export const RelayEscalationNotificationSchema = z.object({
    ...eventBase,
    type: z.literal('relay.escalation'),
    bidId: z.string().min(1),
    carId: z.string().min(1),
    /** ISO-8601 LIVE auction end. */
    closesAt: z.string().min(1),
    /** Whole minutes left when the job fired (≥ 1). */
    minutesLeft: z.number().int().min(0),
    claimedBy: z.string().nullable(),
});
export const NotificationEventSchema = z.discriminatedUnion('type', [
    KycDocumentSubmittedNotificationSchema,
    RelayEscalationNotificationSchema,
    SavedSearchMatchedNotificationSchema,
    RelayRejectedNotificationSchema,
    DealOverdueNotificationSchema,
    InfraPaymentDueNotificationSchema,
    BidStatusChangedNotificationSchema,
    BidInsertedNotificationSchema,
    BidUpdatedNotificationSchema,
    RelayAmountChangedNotificationSchema,
    RelayAutoCancelledNotificationSchema,
    RelayOutcomeNotificationSchema,
    KycStatusChangedNotificationSchema,
    KycDocumentRequestedNotificationSchema,
    DealStateChangedNotificationSchema,
    UserRegisteredNotificationSchema,
    UserEmailVerificationRequestedNotificationSchema,
    UserPasswordResetRequestedNotificationSchema,
    TenderClosingSoonNotificationSchema,
    RelayExpiredNotificationSchema,
    DepositTopupReminderNotificationSchema,
    DepositTopupRequestedNotificationSchema,
    DepositTopupConfirmedNotificationSchema,
    DepositTopupRejectedNotificationSchema,
    InvoicePaymentReminderNotificationSchema,
    DealDepositForfeitedNotificationSchema,
    PaymentReceivedNotificationSchema,
    InvoiceIssuedNotificationSchema,
]);
const TOPIC_BY_TYPE = {
    'bid.status_changed': 'bids',
    'bid.inserted': 'bids',
    'bid.updated': 'bids',
    'relay.amount_changed': 'bids',
    'relay.auto_cancelled': 'bids',
    'relay.outcome': 'bids',
    'tender.closing_soon': 'bids',
    'relay.expired': 'bids',
    'relay.rejected': 'bids',
    'kyc.status_changed': 'kyc',
    'kyc.document_requested': 'kyc',
    'deal.state_changed': 'deals',
    'deal.overdue': 'deals',
    // Staff-only, like `deal.overdue`; no client preference ever gates it.
    'infra.payment_due': 'deals',
    'user.registered': 'account',
    'user.email_verification_requested': 'account',
    'user.password_reset_requested': 'account',
    // A deposit is what a client bids with, so a top-up reminder rides the
    // transactional `bids` topic (it cannot be switched off in-app).
    'deposit.topup_reminder': 'bids',
    // Client finance (CFB phase 3): same topic as the reminder — a top-up is
    // what a client bids with.
    'deposit.topup_requested': 'bids',
    'deposit.topup_confirmed': 'bids',
    'deposit.topup_rejected': 'bids',
    // An invoice bills a won car — the deal — so its payment reminder rides the
    // transactional `deals` topic.
    'invoice.payment_reminder': 'deals',
    // Decision (a): the deposit withheld on a refused car — about the deal.
    'deal.deposit_forfeited': 'deals',
    // Admin client finances: money received and allocated — about the deposit a
    // client bids with (and invoices), so the transactional `bids` topic, like
    // `deposit.topup_confirmed`.
    'payment.received': 'bids',
    // mobile-app-expo phase 3: its own toggleable topic, default on.
    'saved_search.matched': 'saved_search',
    // An issued invoice bills a won car — the deal — like its payment reminder.
    'invoice.issued': 'deals',
    // admin-mobile-app phase 5: staff-only events of the admin push channel. No
    // client channel carries them, so the topic never gates a client delivery.
    'kyc.document_submitted': 'kyc',
    'relay.escalation': 'bids',
};
/** Every event type the dispatcher understands — used to classify outbox rows. */
export const NOTIFICATION_EVENT_TYPES = Object.keys(TOPIC_BY_TYPE);
/** True when an outbox row's `type` names an event the dispatcher can deliver. */
export function isNotificationEventType(type) {
    return Object.prototype.hasOwnProperty.call(TOPIC_BY_TYPE, type);
}
/** The preference topic an event is gated by. */
export function notificationTopicOf(type) {
    return TOPIC_BY_TYPE[type];
}
/** The client this event is ABOUT, when it is about one. */
export function notificationSubjectUserId(event) {
    if ('userId' in event && typeof event.userId === 'string' && event.userId)
        return event.userId;
    return null;
}
//# sourceMappingURL=notification-events.js.map